import { collection, getDocs, type CollectionReference } from 'firebase/firestore';

import { db } from '@/firebase-config';
import { buildProjectCollectionPath, buildProjectSubcollectionPath } from '@/features/sync/project-scope';
import type { AdminProjectOverview, AdminProjectsClient } from '../contracts';
import { isRecord, readString, timestampIso } from './firestore-read-normalizers';

type ProjectSummaryDoc = {
  showTitle?: string;
  title?: string;
  ownerId?: string;
  lastSync?: string;
  status?: string;
  archivedAt?: unknown;
  closingDate?: string | null;
  deleted?: boolean;
  deletedAt?: unknown;
};

type ProjectDocumentSnapshot = {
  id: string;
  data: () => unknown;
};

type ProjectSnapshot = {
  docs: ProjectDocumentSnapshot[];
};

type MemberDoc = {
  status?: string;
};

type MemberSnapshot = {
  docs: Array<{ data: () => MemberDoc }>;
  size?: number;
};

type MemberSummary = Pick<AdminProjectOverview, 'memberCount' | 'pendingApprovals'>;

const EMPTY_MEMBER_SUMMARY: MemberSummary = {
  memberCount: 0,
  pendingApprovals: 0,
};

function projectTitle(data: ProjectSummaryDoc): string {
  return data.showTitle || data.title || 'Untitled Project';
}

function isSoftDeletedProject(data: ProjectSummaryDoc): boolean {
  return data.deleted === true || data.status === 'deleted' || data.deletedAt != null;
}

function toAdminProjectOverview(
  orgId: string,
  projectDoc: ProjectDocumentSnapshot,
  memberSummary: MemberSummary,
): AdminProjectOverview {
  const data = toProjectSummaryDoc(projectDoc.data());

  return {
    orgId,
    id: projectDoc.id,
    title: projectTitle(data),
    ownerId: data.ownerId || '',
    memberCount: memberSummary.memberCount,
    pendingApprovals: memberSummary.pendingApprovals,
    lastSync: data.lastSync || 'Never',
    status: data.status === 'archived' ? 'archived' : 'active',
    archivedAt: timestampIso(data.archivedAt),
    closingDate: data.closingDate || null,
  };
}

function toProjectSummaryDoc(value: unknown): ProjectSummaryDoc {
  const data = isRecord(value) ? value : {};

  return {
    showTitle: readString(data.showTitle),
    title: readString(data.title),
    ownerId: readString(data.ownerId),
    lastSync: readString(data.lastSync),
    status: readString(data.status),
    archivedAt: data.archivedAt,
    closingDate: typeof data.closingDate === 'string' ? data.closingDate : null,
    deleted: data.deleted === true,
    deletedAt: data.deletedAt,
  };
}

function countPendingApprovals(snapshot: MemberSnapshot): number {
  return snapshot.docs.filter(memberDoc => memberDoc.data().status === 'pending').length;
}

export class FirebaseAdminProjectsClient implements AdminProjectsClient {
  async listAdminProjectOverviews(orgId: string): Promise<AdminProjectOverview[]> {
    const projectsRef = collection(db, buildProjectCollectionPath(orgId)) as CollectionReference<ProjectSummaryDoc>;
    const projectSnapshot = await getDocs(projectsRef) as ProjectSnapshot;
    const visibleProjectDocs = projectSnapshot.docs.filter(projectDoc => (
      !isSoftDeletedProject(toProjectSummaryDoc(projectDoc.data()))
    ));

    return await Promise.all(
      visibleProjectDocs.map(async projectDoc => (
        toAdminProjectOverview(
          orgId,
          projectDoc,
          await this.readMemberSummary(orgId, projectDoc.id),
        )
      )),
    );
  }

  private async readMemberSummary(orgId: string, projectId: string): Promise<MemberSummary> {
    try {
      const membersRef = collection(
        db,
        buildProjectSubcollectionPath({ orgId, projectId }, 'members'),
      ) as CollectionReference<MemberDoc>;
      const membersSnapshot = await getDocs(membersRef) as MemberSnapshot;

      return {
        memberCount: membersSnapshot.size ?? membersSnapshot.docs.length,
        pendingApprovals: countPendingApprovals(membersSnapshot),
      };
    } catch {
      return EMPTY_MEMBER_SUMMARY;
    }
  }
}
